import dayjs from "dayjs";
import { LEAGUE } from "constants/tournamentTypes";

export const TEAM_MODE = {
  COPY: "copy",
  PICK: "pick",
  LATER: "later",
};

export const CREATE_STEPS = ["identity", "teams", "confirm", "fixtures"];

export const yearFromStartAt = (startAt) => {
  if (!startAt) return null;
  const parsed = dayjs(startAt);
  return parsed.isValid() ? parsed.year() : null;
};

// R5: `features` is readable by every Gestión area (financial/accounts is Finanzas-only). The server pins the
// currency only when a Cobros account sets it (`currency_locked`); otherwise the user keeps choosing.
export const lockedCurrencyFromFeatures = (features) => {
  if (!features?.currency_locked || !features?.currency_code) return null;
  return features.currency_code;
};

export const visibleCreateSteps = (teamMode) => {
  if (teamMode === TEAM_MODE.LATER) {
    return ["identity", "teams"];
  }
  if (teamMode === TEAM_MODE.COPY || teamMode === TEAM_MODE.PICK) {
    return CREATE_STEPS;
  }
  return ["identity", "teams"];
};

export const canAdvanceFromTeams = (teamMode, { copyFromId, teamIds } = {}) => {
  if (teamMode === TEAM_MODE.LATER) return true;
  if (teamMode === TEAM_MODE.COPY) return Boolean(copyFromId);
  if (teamMode === TEAM_MODE.PICK) return Array.isArray(teamIds) && teamIds.length > 0;
  return false;
};

export const canConfirmTeamList = ({ confirmed, teamCount }) => Boolean(confirmed) && Number(teamCount) > 0;

export const MIN_TEAMS_FOR_FIXTURE = 2;

// 2 qualifiers per group give semifinals + final with 2+ groups, and a final with one group. One qualifier
// from a single group would make no bracket at all, so 2 is the default for any group count.
export const defaultAdvancePerGroup = () => 2;

export const knockoutQualifiers = (groupsCount, advancePerGroup) => {
  const total = Math.max(1, Number(groupsCount) || 1) * (Number(advancePerGroup) || 0);
  const stage = { 2: "final", 4: "semis", 8: "quarters" }[total] || "other";
  return { total, stage };
};

export const fixtureNeedsMoreTeams = (teamCount) => teamCount != null && Number(teamCount) < MIN_TEAMS_FOR_FIXTURE;

export const lineupTeamLabel = (lineup) =>
  lineup?.team?.name || lineup?.participant?.name || lineup?.name || `#${lineup?.team_id || lineup?.id}`;

export const buildLeagueCreatePayload = ({
  competitionUuid,
  form,
  teamMode,
  copyFromId,
  teamIds,
  listConfirmed,
  teamCount,
}) => {
  const year = yearFromStartAt(form.start_at);
  const payload = {
    competition_id: competitionUuid,
    name: form.name,
    sport_slug: form.sport_slug,
    category_id: form.category_id,
    start_at: form.start_at,
    year,
    tournament_type: LEAGUE,
    modality: "teams",
    currency_code: form.currency_code,
    requires_document_for_players: false,
    open_signup: false,
    league_setup_mode: teamMode === TEAM_MODE.LATER ? "manual" : "auto_fixture",
    generate_fixtures:
      teamMode === TEAM_MODE.LATER || fixtureNeedsMoreTeams(teamCount) ? false : Boolean(form.generate_fixtures),
    groups_enabled: form.groups_enabled !== false,
    groups_count: form.groups_count ?? 1,
    teams_per_group: form.teams_per_group ?? 4,
    advance_per_group: form.advance_per_group ?? 0,
    group_home_and_away: Boolean(form.group_home_and_away ?? form.is_home_and_away),
    is_home_and_away: Boolean(form.group_home_and_away ?? form.is_home_and_away),
    knockout_enabled: Boolean(form.knockout_enabled),
    knockout_home_and_away: Boolean(form.knockout_home_and_away),
    knockout_final_home_and_away: Boolean(form.knockout_final_home_and_away),
    matchdays_count: form.matchdays_count ?? "",
  };

  if (form.entry_fee_template_id !== "" && form.entry_fee_template_id != null) {
    payload.entry_fee_template_id = form.entry_fee_template_id;
  }

  if (form.new_category_name) {
    payload.new_category_name = form.new_category_name;
    payload.new_category_modality = form.new_category_modality || "teams";
  }

  if (teamMode === TEAM_MODE.COPY && copyFromId && listConfirmed) {
    payload.copy_lineup_from_tournament_id = copyFromId;
  }

  if (teamMode === TEAM_MODE.PICK && listConfirmed) {
    payload.team_ids = teamIds;
  }

  return payload;
};

export const appendLeagueFormData = (formData, payload) => {
  Object.entries(payload).forEach(([key, value]) => {
    if (key === "team_ids" && Array.isArray(value)) {
      value.forEach((id) => formData.append("team_ids[]", id));
      return;
    }
    if (value === undefined) return;
    formData.append(key, value === null ? "" : value);
  });
};

export const TOURNAMENT_NAME_MAX_LENGTH = 100;

// Message id for an invalid tournament name, or null.
export const tournamentNameErrorId = (name) =>
  String(name ?? "").trim().length > TOURNAMENT_NAME_MAX_LENGTH ? "gestion.leagueWizard.nameTooLong" : null;

export const startDateBounds = (now = dayjs()) => ({
  min: dayjs(now).subtract(1, "year").startOf("day"),
  max: dayjs(now).add(2, "year").endOf("day"),
});

/**
 * Message id for an implausible start date on a new tournament, or null. Edits keep the
 * saved date valid so old tournaments stay editable.
 */
export const startDateErrorId = (startAt, { originalStartAt = null, now = dayjs() } = {}) => {
  if (!startAt) return null;
  const date = dayjs(startAt);
  if (!date.isValid()) return "gestion.leagueWizard.startDateInvalid";
  if (originalStartAt && date.isSame(dayjs(originalStartAt), "day")) return null;
  const { min, max } = startDateBounds(now);
  if (date.isBefore(min)) return "gestion.leagueWizard.startDateTooOld";
  if (date.isAfter(max)) return "gestion.leagueWizard.startDateTooFar";
  return null;
};

// Draft of the create wizard, kept per competition so Back or a reload doesn't lose it.
const DRAFT_VERSION = 1;

export const wizardDraftKey = (scopeId) => (scopeId ? `reva-league-wizard-draft:${scopeId}` : null);

// Resolved lazily: touching window.sessionStorage itself throws when storage is blocked.
const sessionStore = (storage) => storage ?? window.sessionStorage;

export const readWizardDraft = (key, storage) => {
  if (!key) return null;
  try {
    const draft = JSON.parse(sessionStore(storage).getItem(key) || "null");
    return draft?.version === DRAFT_VERSION ? draft : null;
  } catch {
    return null;
  }
};

export const writeWizardDraft = (key, draft, storage) => {
  if (!key) return;
  try {
    sessionStore(storage).setItem(key, JSON.stringify({ ...draft, version: DRAFT_VERSION }));
  } catch {
    // storage full or blocked: the wizard still works, it just can't be restored
  }
};

export const clearWizardDraft = (key, storage) => {
  if (!key) return;
  try {
    sessionStore(storage).removeItem(key);
  } catch {
    // ignore
  }
};

export const wizardDraftHasContent = (draft) =>
  Boolean(
    draft &&
      (draft.formData?.name?.trim() ||
        draft.formData?.sport_slug ||
        draft.formData?.start_at ||
        draft.teamMode ||
        draft.selectedTeams?.length),
  );
